// THE single place that decides gift-line mutations for the new drawer.
// Replaces (flag-on only): freeItemPopup auto-add, the 1s setTimeout cleanup in
// useDiscountFreeItem, and Phase 1's useGiftAutoAdd. Pure: no IO, fully
// unit-tested (bug #945 lives here).

export const parseRemovedIds = raw => {
  if (!raw) return [];
  if (raw === 'true') return ['__all__']; // backward compat: old format
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const pruneExpiredRemovals = (removedIds, incentives) => {
  const satisfied = new Set(
    (incentives || [])
      .filter(i => i.type === 'freeItem' && i.threshold?.satisfied)
      .map(i => i.discountId)
  );
  return (removedIds || []).filter(id =>
    id === '__all__' ? satisfied.size > 0 : satisfied.has(id)
  );
};

const FREE_ITEM_ATTRS = discountId => {
  const attrs = [{ key: '_freeItem', value: 'true' }];
  if (discountId) attrs.push({ key: '_freeItemDiscountId', value: discountId });
  return attrs;
};

const isFreeItemLine = node => node?.attributes?.some(a => a.key === '_freeItem');

const lineMatchesIncentive = (node, incentive) =>
  incentive.reward?.products?.some(p => p.variantId === node?.merchandise?.id);

// The cart line node holding the chosen gift for a choice group: matched by
// variant, scoped to the group's discountId when the line carries one. Shared
// by GiftChoicePanel (which line is selected) and useCartIncentives.selectGift.
export const findGiftLineForGroup = (lines, group) =>
  (lines || []).find(({ node }) => {
    const inGroup = group.freeProducts.some(p => p.variantId === node.merchandise?.id);
    if (!inGroup) return false;
    const idAttr = node.attributes?.find(a => a.key === '_freeItemDiscountId')?.value;
    if (idAttr) return idAttr === group.discountId;
    return node.attributes?.some(a => a.key === '_freeItem');
  })?.node;

export function reconcileGiftLines({ lines, incentives, removedIds, scaleGiftQuantity }) {
  const cartLines = lines || [];
  const freeItemIncentives = (incentives || []).filter(i => i.type === 'freeItem');
  const satisfied = freeItemIncentives.filter(i => i.threshold?.satisfied);
  const isRemoved = id => removedIds.includes('__all__') || removedIds.includes(id);

  // --- cleanup: any free-item gift line not justified by a satisfied incentive goes ---
  const itemsToRemove = cartLines
    .filter(({ node }) => isFreeItemLine(node))
    .filter(({ node }) => !satisfied.some(i => lineMatchesIncentive(node, i)))
    .map(({ node }) => ({ id: node.id, variantId: node?.merchandise?.id, quantity: 0 }));

  // --- auto-add / scale ---
  // Default (scaleGiftQuantity off): a condition satisfied multiple times never
  // auto-adds a second gift — matches Phase 1, incentive.itemsLeft still reports
  // extra claims. With scaleGiftQuantity on (US pump-per-liter, #1015), the gift
  // line follows the entitlement for quantity-kind conditions, capped at
  // usesPerOrderLimit, topping up AND reducing.
  const itemsToAdd = [];
  const itemsToUpdate = [];
  satisfied.forEach(incentive => {
    const single = incentive.reward?.products?.length === 1;
    if (!single || isRemoved(incentive.discountId)) return;
    const scalable = scaleGiftQuantity && incentive.threshold?.kind === 'quantity';
    const entitled = scalable
      ? Math.min(incentive.timesSatisfiedStrict || 1, incentive.usesPerOrderLimit || Infinity)
      : 1;
    const giftLines = cartLines
      .filter(({ node }) => isFreeItemLine(node) && lineMatchesIncentive(node, incentive))
      .map(({ node }) => node);
    if (!giftLines.length) {
      itemsToAdd.push({
        variantId: incentive.reward.products[0].variantId,
        quantity: entitled,
        customAttributes: FREE_ITEM_ATTRS(incentive.discountId),
      });
    } else if (scalable) {
      const owned = giftLines.filter(({ attributes }) =>
        attributes.every(a => a.key !== '_freeItemDiscountId' || a.value === incentive.discountId)
      );
      if (!owned.length) return;
      // Shopify splits a gift line into discounted + undiscounted lines when
      // the BXGY discount covers only part of its quantity — consolidate back
      // to one line holding the full entitlement, drop the extras
      const [first, ...extras] = owned;
      if (first.quantity !== entitled) {
        itemsToUpdate.push({ id: first.id, variantId: first.merchandise?.id, quantity: entitled });
      }
      extras.forEach(line => {
        itemsToUpdate.push({ id: line.id, variantId: line.merchandise?.id, quantity: 0 });
      });
    }
  });

  // --- choice groups (CtaSlot / GiftChoicePanel) ---
  // Groups stay listed after a gift is chosen (filter on satisfied, not itemsLeft)
  // so the GiftChoicePanel can still show the current selection. `selected` flags
  // groups whose gift is already in the cart: the CtaSlot hides the choose-gift
  // button for those and only shows it again once the shopper deletes the gift
  // (deletion flags the discountId removed, bringing the group back unselected).
  const choiceGroups = satisfied
    .filter(
      i => i.reward?.products?.length && (i.reward.products.length > 1 || isRemoved(i.discountId))
    )
    .map(i => ({
      giftType: 'freeItem',
      discountId: i.discountId,
      discountTitle: i.title,
      freeProducts: i.reward.products,
      selected: cartLines.some(({ node }) => isFreeItemLine(node) && lineMatchesIncentive(node, i)),
    }));

  return { itemsToAdd, itemsToRemove, itemsToUpdate, choiceGroups };
}

export default reconcileGiftLines;
